/**
 * Event Exchange arithmetic for the admin screens — the same rules the server
 * uses (px/src/common/exchange-math.js), so a preview never promises a user
 * more than the server will pay.
 *
 *   rate     entered as "X event points = Y coins"; stored as event points per
 *            1 coin (`points_per_unit`)
 *   limits   entered in the coin ("min 1 PX, max 500 USDT"); the event points
 *            they need are worked out from the rate — min rounds up, max down
 *   payouts  round down to the currency's decimals
 */

export const fmt = (n, max = 8) => Number(n).toLocaleString("en-US", { maximumFractionDigits: max });

const clampDecimals = (decimals) => Math.min(8, Math.max(0, Number.isInteger(Number(decimals)) ? Number(decimals) : 4));

/** Float noise off before rounding. */
const clean = (value) => Number(Number(value).toPrecision(15));

/** Round down to the currency's decimals. */
export const roundDown = (value, decimals) => {
  const scale = 10 ** clampDecimals(decimals);
  return Math.floor(clean(value * scale)) / scale;
};

/** The smallest amount a currency can pay: 1, 0.1, ... 0.00000001. */
export const smallestUnit = (decimals) => 1 / 10 ** clampDecimals(decimals);

/**
 * "X event points = Y coins" -> event points per 1 coin. Null when either side
 * is empty or not positive.
 */
export const pointsPerUnitOf = (ratePoints, rateUnits) => {
  const points = Number(ratePoints);
  const units = Number(rateUnits);
  if (ratePoints === "" || ratePoints === null || ratePoints === undefined) return null;
  if (!(points > 0) || !(units > 0)) return null;
  return Number((points / units).toFixed(6));
};

const emptyToNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

/**
 * The event points the limits need at this rate.
 *
 * @param {number|null} perUnit       event points per 1 coin
 * @param {string|number} minUnits    "" = the currency's smallest amount
 * @param {string|number} maxUnits    "" = no limit
 * @param {number} decimals
 */
export const limitPoints = (perUnit, minUnits, maxUnits, decimals) => {
  if (!(perUnit > 0)) return { minPoints: null, maxPoints: null, minUnits: null, maxUnits: null };
  const min = Math.max(emptyToNull(minUnits) || 0, smallestUnit(decimals));
  const max = emptyToNull(maxUnits);
  return {
    minUnits: min,
    maxUnits: max,
    minPoints: Math.max(1, Math.ceil(clean(min * perUnit))),
    maxPoints: max == null ? null : Math.floor(clean(max * perUnit)),
  };
};

/** What the user receives for `points`, after the fee. */
export const unitsFor = (points, perUnit, feePercentage, decimals) =>
  perUnit > 0 ? roundDown((points / perUnit) * (1 - (Number(feePercentage) || 0) / 100), decimals) : 0;

export default { fmt, roundDown, smallestUnit, pointsPerUnitOf, limitPoints, unitsFor };
